import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { API_CONFIG, getNodeApiHeaders } from '../../helpers/constants';


interface TokenResponse {
  expires_in?: number;
  token_type: string;
  scope?: string;
  provider: string;
  connection_settings: {
    client_id: string;
    client_secret: string;
    provider: string;
    auth_url: string;
    token_url: string;
    access_token: string;
    refresh_token?: string;
    expires_in?: number;
    token_type: string;
    scope?: string;
    [key: string]: any;
  };
}

const UnifiedOAuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tokenData, setTokenData] = useState<TokenResponse | null>(null);

  // Add immediate loading overlay to prevent empty screen
  React.useLayoutEffect(() => {
    // This runs synchronously before the browser paints
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);


  useEffect(() => {
    // Start OAuth processing immediately without any delays
    const handleOAuthCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error) {
          const errorMessage = `OAuth error: ${error}${errorDescription ? ` - ${errorDescription}` : ''}`;
          setError(errorMessage);
          setLoading(false);
          
          // Send error to parent window
          if (window.opener) {
            window.opener.postMessage({
              type: 'OAUTH_ERROR',
              error: errorMessage
            }, window.location.origin);
            window.close();
          }
          return;
        }

        if (!code) {
          const errorMessage = 'No authorization code received';
          setError(errorMessage);
          setLoading(false);
          
          // Send error to parent window
          if (window.opener) {
            window.opener.postMessage({
              type: 'OAUTH_ERROR',
              error: errorMessage
            }, window.location.origin);
            window.close();
          }
          return;
        }

        if (!state || state.trim() === '') {
          const errorMessage = 'No state parameter received';
          setError(errorMessage);
          setLoading(false);
          
          // Send error to parent window
          if (window.opener) {
            window.opener.postMessage({
              type: 'OAUTH_ERROR',
              error: errorMessage
            }, window.location.origin);
            window.close();
          }
          return;
        }

        // Get stored OAuth data from localStorage with multiple attempts
        let tempDataStr = null;
        const maxRetries = 3;
        
        for (let i = 0; i < maxRetries; i++) {
          // Try generic key first
          tempDataStr = localStorage.getItem('oauth_temp_data');
          
          // Try state-specific key
          if (!tempDataStr && state && state.trim() !== '') {
            tempDataStr = localStorage.getItem(`oauth_${state}`);
          }
          
          // If found, break
          if (tempDataStr) break;
          
          // Small delay only if not found and not last attempt
          if (i < maxRetries - 1) {
            await new Promise(resolve => setTimeout(resolve, 50));
          }
        }
        
        // If localStorage still fails, try to get data from parent window
        if (!tempDataStr && window.opener) {
          try {
            window.opener.postMessage({
              type: 'REQUEST_OAUTH_DATA',
              state: state
            }, window.location.origin);
            
            // Wait for response
            const parentData = await new Promise((resolve, reject) => {
              const timeout = setTimeout(() => reject(new Error('Timeout')), 2000); // Reduced from 3000ms to 2000ms
              
              const handleMessage = (event: MessageEvent) => {
                if (event.origin !== window.location.origin) return;
                
                if (event.data.type === 'OAUTH_DATA_RESPONSE' && event.data.state === state) {
                  clearTimeout(timeout);
                  window.removeEventListener('message', handleMessage);
                  resolve(event.data.data);
                }
              };
              
              window.addEventListener('message', handleMessage);
            });
            
            if (parentData && typeof parentData === 'string') {
              tempDataStr = parentData;
            }
          } catch (parentError) {
            // Parent window communication failed, continue with state parameter fallback
          }
        }
        
        if (!tempDataStr) {
          // Try to parse state parameter as fallback
          try {
            if (!state || state.trim() === '') {
              throw new Error('No state parameter available');
            }
            
            const [statePayloadB64] = state.split('.');
            const stateData = JSON.parse(atob(statePayloadB64 || ''));

            // Validate state data has required fields
            if (!stateData || typeof stateData !== 'object' || !stateData.provider) {
              throw new Error('Invalid state data structure');
            }
            
            // Use state data directly if localStorage is not available
            const tempData = {
              ...stateData,
              // Ensure we have a redirectUri
              redirectUri: stateData.redirectUri || `${window.location.origin}/auth/unified/callback`
            };
            
            // Continue with the normal flow using state data

            // Construct redirect URI
            const redirectUri = `${window.location.origin}/auth/unified/callback`;

            // Exchange code for tokens
            const response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/oauth/exchange-token`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...getNodeApiHeaders(),
              },
              body: JSON.stringify({
                provider: tempData.provider,
                code,
                redirectUri,
                clientId: tempData.clientId,
                clientSecret: tempData.clientSecret,
                customParams: tempData.customParams,
                appId: tempData.appId,
                channelId: tempData.channelId,
                state,
                codeVerifier: tempData.codeVerifier
              }),
            });

            if (!response.ok) {
              const errorData = await response.json();
              throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
            }

            const tokenResponse: TokenResponse = await response.json();
            setTokenData(tokenResponse);
            setLoading(false);

            // Send success data back to parent window and close
            if (window.opener) {
              window.opener.postMessage({
                type: 'OAUTH_SUCCESS',
                data: {
                  provider: tempData.provider,
                  appId: tempData.appId,
                  channelId: tempData.channelId,
                  connection_settings: tokenResponse.connection_settings,
                  access_token: tokenResponse.connection_settings.access_token,
                  refresh_token: tokenResponse.connection_settings.refresh_token,
                  expires_in: tokenResponse.expires_in,
                  token_type: tokenResponse.token_type,
                  scope: tokenResponse.scope
                }
              }, window.location.origin);
              
              // Close popup after a short delay to ensure message is sent
              setTimeout(() => {
                window.close();
              }, 500);
            }
            return;
            
          } catch (stateParseError) {
            // Try alternative parsing methods
            try {
              // Maybe it's URL encoded
              const decodedState = decodeURIComponent(state);
              const [decodedStatePayloadB64] = decodedState.split('.');
              const stateData = JSON.parse(atob(decodedStatePayloadB64 || ''));
              
              // Continue with the normal flow using decoded state data
              // ... (same code as above)
              
            } catch (alternativeParseError) {
              const errorMessage = 'Authentication session expired. Please try connecting again.';
              setError(errorMessage);
              setLoading(false);
              
              // Send error to parent window
              if (window.opener) {
                window.opener.postMessage({
                  type: 'OAUTH_ERROR',
                  error: errorMessage
                }, window.location.origin);
                window.close();
              }
              return;
            }
          }
        }

        let tempData;
        try {
          
          // Clean the data string if needed
          let cleanDataStr = tempDataStr?.trim();
          if (!cleanDataStr) {
            throw new Error('Empty session data string');
          }
          
          tempData = JSON.parse(cleanDataStr);
          
          if (!tempData || typeof tempData !== 'object' || Array.isArray(tempData)) {
            throw new Error(`Invalid temp data format - expected object, got ${typeof tempData}`);
          }
          
          // Validate required fields
          if (!tempData.provider || !tempData.clientId || !tempData.clientSecret) {
            throw new Error(`Missing required fields: provider=${!!tempData.provider}, clientId=${!!tempData.clientId}, clientSecret=${!!tempData.clientSecret}`);
          }
          
        } catch (parseError) {
          const errorMessage = 'Authentication data is corrupted. Please try connecting again.';
          setError(errorMessage);
          setLoading(false);
          
          if (window.opener) {
            window.opener.postMessage({
              type: 'OAUTH_ERROR',
              error: errorMessage
            }, window.location.origin);
            window.close();
          }
          return;
        }
        
        // Verify state matches (if both exist)
        if (tempData.state && state && tempData.state !== state) {
          const errorMessage = 'Invalid OAuth state parameter';
          setError(errorMessage);
          setLoading(false);
          
          // Send error to parent window
          if (window.opener) {
            window.opener.postMessage({
              type: 'OAUTH_ERROR',
              error: errorMessage
            }, window.location.origin);
            window.close();
          }
          return;
        }

        // Clean up localStorage after successful connection
        localStorage.removeItem('oauth_temp_data');
        if (state && state.trim() !== '') {
          localStorage.removeItem(`oauth_${state}`);
        }

        // Construct redirect URI - must match exactly what was used in authorization request
        const redirectUri = `${window.location.origin}/auth/unified/callback`;

        // For Zoho CRM, extract location and accounts-server from callback URL
        // Zoho redirects with these parameters that must be used for token exchange
        // IMPORTANT: We preserve the user's original region selection for connection_settings,
        // but use accounts-server URL for the actual token exchange request
        let updatedCustomParams = { ...tempData.customParams };
        if (tempData.provider === 'zoho_crm') {
          const location = searchParams.get('location');
          const accountsServer = searchParams.get('accounts-server');
          
          if (accountsServer) {
            // Store the accounts-server URL for token exchange (required by Zoho)
            // This will be used ONLY for the token exchange request, not for connection_settings
            updatedCustomParams.accountsServer = accountsServer;
          } else if (location) {
            // Fallback: if no accounts-server but location is provided, update region
            // This should rarely happen, but handle it just in case
            const regionMatch = location.match(/^[a-z.]+$/);
            if (regionMatch) {
              updatedCustomParams.region = location;
            }
          }
          // Note: We intentionally do NOT overwrite the user's selected region
          // The accounts-server is only used for the token exchange request
        }

        // Exchange code for tokens
        const response = await fetch(`${API_CONFIG.NODE_BASE_URL}/rest/oauth/exchange-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getNodeApiHeaders(),
          },
          body: JSON.stringify({
            provider: tempData.provider,
            code,
            redirectUri,
            clientId: tempData.clientId,
            clientSecret: tempData.clientSecret,
            customParams: updatedCustomParams,
            appId: tempData.appId,
            channelId: tempData.channelId,
            state,
            codeVerifier: tempData.codeVerifier
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const tokenResponse: TokenResponse = await response.json();
        
        // Send success message to parent window immediately
        if (window.opener) {
          window.opener.postMessage({
            type: 'OAUTH_SUCCESS',
            data: {
              provider: tempData.provider,
              appId: tempData.appId,
              channelId: tempData.channelId,
              connection_settings: tokenResponse.connection_settings,
              access_token: tokenResponse.connection_settings.access_token,
              refresh_token: tokenResponse.connection_settings.refresh_token,
              expires_in: tokenResponse.expires_in,
              token_type: tokenResponse.token_type,
              scope: tokenResponse.scope
            }
          }, window.location.origin);
        }

        // Show success message to user
        setTokenData(tokenResponse);
        setLoading(false);
        
        // Close popup after 8 seconds to let user see success message
        setTimeout(() => {
          if (window.opener) {
            window.close();
          }
        }, 8000);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        setLoading(false);
        
        // Send error to parent window
        if (window.opener) {
          window.opener.postMessage({
            type: 'OAUTH_ERROR',
            error: errorMessage
          }, window.location.origin);
          window.close();
        }
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate]);

  // Auto-redirect to home if not in popup after 5 seconds
  useEffect(() => {
    if (!window.opener && !loading) {
      const timer = setTimeout(() => {
        navigate('/');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [loading, navigate]);


  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      sx={{ 
        p: 3,
        backgroundColor: '#fafafa',
        color: '#000',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 9999
      }}
    >
      {loading ? (
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
          <CircularProgress size={60} sx={{ mb: 3, color: '#1976d2' }} />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: '#000', mb: 2 }}>
            Connecting your account...
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', lineHeight: 1.6 }}>
            Please wait while we complete the connection.
            <br />
            This window will close automatically.
          </Typography>
        </Box>
      ) : error ? (
        <>
          <Alert severity="error" sx={{ mb: 3, maxWidth: 500 }}>
            <Typography variant="subtitle1" gutterBottom>
              Authentication Failed
            </Typography>
            <Typography variant="body2">
              {error}
            </Typography>
          </Alert>
          {!window.opener && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              You will be redirected to the home page in a few seconds.
            </Typography>
          )}
        </>
      ) : tokenData ? (
        <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
          <Alert severity="success" sx={{ mb: 3, maxWidth: 500 }}>
            <Typography variant="subtitle1" gutterBottom>
              Connection Successful! ✅
            </Typography>
            <Typography variant="body2">
              Your {tokenData.provider} account has been connected successfully.
            </Typography>
          </Alert>
          <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', mt: 2 }}>
            This window will close automatically in 8 seconds.
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default UnifiedOAuthCallback;
